import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import { Image as ImageIcon, Plus, Trash2 } from 'lucide-react';

import CategoryEditorDialog from '../components/CategoryEditorDialog';
import CategoryImageDialog from '../components/CategoryImageDialog';
import ScheduleManager from '../components/ScheduleManager';
import { LoadingIndicator } from '../components/assets';
import { deleteCategory, getCategories, updateCategory } from '../utils/api';

const panelShadow = (theme, intensity = 0.6) => (
  `0 8px 15px ${alpha(theme.palette.secondary.main, intensity)}, ` +
  `0 20px 40px ${alpha(theme.palette.secondary.main, intensity / 2)}, ` +
  `0 35px 65px ${alpha(theme.palette.secondary.main, intensity / 6)}`
);

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [imageCategory, setImageCategory] = useState(null);
  const [savingColorId, setSavingColorId] = useState(null);
  const [scheduleRefreshKey, setScheduleRefreshKey] = useState(0);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setError('');
    try {
      const response = await getCategories();
      setCategories(response.categories || []);
    } catch (err) {
      setError(err.data?.message || err.message || 'Unable to load categories.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const refresh = () => load({ quiet: true });
    window.addEventListener('assistantUpdate', refresh);
    return () => window.removeEventListener('assistantUpdate', refresh);
  }, [load]);

  const replaceCategory = (saved) => {
    setCategories((current) => (
      [...current.filter((category) => category.id !== saved.id), saved]
        .sort((left, right) => left.name.localeCompare(right.name))
    ));
  };

  const changeColor = async (category, color) => {
    setSavingColorId(category.id);
    setError('');
    try {
      replaceCategory(await updateCategory(category.id, { color }));
    } catch (err) {
      setError(err.data?.message || err.message || 'Unable to update the category color.');
    } finally {
      setSavingColorId(null);
    }
  };

  const remove = async (category) => {
    if (!window.confirm(`Delete “${category.name}”? Its tasks become uncategorized and its schedule blocks are removed.`)) {
      return;
    }
    try {
      await deleteCategory(category.id);
      setCategories((current) => current.filter((item) => item.id !== category.id));
      setScheduleRefreshKey((current) => current + 1);
    } catch (err) {
      setError(err.data?.message || err.message || 'Unable to delete category.');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3, pb: 18 }}>
      <Stack spacing={4}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ sm: 'center' }}
          spacing={2}
        >
          <Box>
            <Typography variant="h4" sx={{ color: 'primary.main' }}>Categories</Typography>
            <Typography variant="body2" sx={{ mt: 1, color: 'secondary.main' }}>
              Organize tasks and choose where your time goes.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => setDialogOpen(true)}
            sx={(theme) => ({
              m: 0,
              color: theme.palette.background.default,
              bgcolor: theme.palette.primary.main,
              boxShadow: `0 5px 0 ${darken(theme.palette.primary.main, 0.55)}`,
              '&:hover': {
                color: theme.palette.background.default,
                bgcolor: theme.palette.primary.main,
                transform: 'translate(-2px, -2px)',
                boxShadow: `2px 7px 0 ${darken(theme.palette.primary.main, 0.55)}`,
              },
              '&:active': {
                transform: 'translateY(3px)',
                boxShadow: `0 2px 0 ${darken(theme.palette.primary.main, 0.55)}`,
              },
            })}
          >
            Add new category
          </Button>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        <Box component="section">
          <Typography variant="h6" sx={{ mb: 2.5, color: 'secondary.main' }}>
            Your categories [{categories.length}]
          </Typography>
          {loading ? <LoadingIndicator text="loading categories" /> : (
            <Stack spacing={2}>
              {!categories.length && (
                <Box
                  sx={(theme) => ({
                    p: 3,
                    textAlign: 'center',
                    border: `3px solid ${theme.palette.green?.main || '#39FF14'}`,
                    borderRadius: 4,
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: panelShadow(theme, 0.25),
                  })}
                >
                  <Typography variant="body2" sx={{ color: 'secondary.main' }}>
                    No categories yet. Add one when it would make choosing your next task easier.
                  </Typography>
                </Box>
              )}

              {categories.map((category) => (
                <Box
                  key={category.id}
                  sx={(theme) => ({
                    position: 'relative',
                    width: '100%',
                    minHeight: { xs: 150, sm: 180 },
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    border: `3px solid ${theme.palette.green?.main || '#39FF14'}`,
                    borderRadius: 4,
                    background:
                      `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.96)} 0%, ` +
                      `${theme.palette.background.default} 72%, ${alpha(theme.palette.primary.main, 0.08)} 100%)`,
                    boxShadow: panelShadow(theme, 0.6),
                    transition: 'transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
                    '&:hover': {
                      transform: 'translate(-3px, -3px)',
                      borderColor: theme.palette.green?.main || '#39FF14',
                      boxShadow:
                        `7px 7px 0 ${alpha(theme.palette.green?.main || '#39FF14', 0.38)}, ` +
                        `${panelShadow(theme, 0.75)}`,
                    },
                    '&:hover .category-card-image': { transform: 'scale(1.06)' },
                  })}
                >
                  {category.image?.thumbnail_url && (
                    <Box
                      data-testid="category-image-backdrop"
                      sx={(theme) => ({
                        position: 'absolute',
                        inset: '0 auto 0 0',
                        width: { xs: '52%', sm: 270 },
                        overflow: 'hidden',
                        color: theme.palette.secondary.main,
                        backgroundColor: theme.palette.background.default,
                        WebkitMaskImage: 'linear-gradient(to right, #000 58%, transparent 100%)',
                        maskImage: 'linear-gradient(to right, #000 58%, transparent 100%)',
                      })}
                    >
                      <Box
                        className="category-card-image"
                        component="img"
                        src={category.image.thumbnail_url}
                        alt=""
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'block',
                          objectFit: 'cover',
                          objectPosition: 'left center',
                          transition: 'transform 220ms ease',
                        }}
                      />
                    </Box>
                  )}

                  <Box
                    sx={{
                      position: 'relative',
                      zIndex: 1,
                      ml: category.image?.thumbnail_url ? { xs: '30%', sm: '190px' } : 0,
                      minWidth: 0,
                      flex: 1,
                      py: { xs: 2, sm: 2.5 },
                      pl: category.image?.thumbnail_url ? 0 : { xs: 2, sm: 3 },
                      pr: { xs: 1.5, sm: 2.5 },
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: { xs: 1, sm: 2 },
                    }}
                  >
                    <Typography
                      variant="h6"
                      title={category.name}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        color: 'primary.main',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {category.name}
                    </Typography>
                    <Box
                      component="input"
                      type="color"
                      title="Category color"
                      aria-label={`Change ${category.name} color`}
                      value={category.color || '#00e5ff'}
                      disabled={savingColorId === category.id}
                      onChange={(event) => changeColor(category, event.target.value)}
                      sx={(theme) => ({
                        flex: '0 0 auto',
                        width: { xs: 48, sm: 56 },
                        height: { xs: 48, sm: 56 },
                        p: 0.5,
                        cursor: 'pointer',
                        border: `2px solid ${category.color || theme.palette.primary.main}`,
                        borderRadius: 2,
                        backgroundColor: theme.palette.background.default,
                      })}
                    />
                    <Box
                      component="button"
                      type="button"
                      title="Category image"
                      aria-label={`Change ${category.name} image`}
                      onClick={() => setImageCategory(category)}
                      sx={(theme) => ({
                        flex: '0 0 auto',
                        width: { xs: 48, sm: 56 },
                        height: { xs: 48, sm: 56 },
                        p: 0,
                        m: 0,
                        display: 'grid',
                        placeItems: 'center',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        color: theme.palette.primary.main,
                        border: `2px solid ${theme.palette.primary.main}`,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.background.default, 0.86),
                      })}
                    >
                      {category.image?.thumbnail_url ? (
                        <Box
                          component="img"
                          src={category.image.thumbnail_url}
                          alt=""
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : <ImageIcon size={23} />}
                    </Box>
                  </Box>

                  <IconButton
                    aria-label={`Delete ${category.name}`}
                    onClick={() => remove(category)}
                    sx={(theme) => ({
                      position: 'absolute',
                      zIndex: 3,
                      top: 8,
                      right: 8,
                      color: theme.palette.secondary.main,
                      backgroundColor: alpha(theme.palette.background.default, 0.82),
                    })}
                  >
                    <Trash2 size={18} />
                  </IconButton>
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        {!loading && (
          <ScheduleManager
            categories={categories}
            onCategoriesChange={setCategories}
            refreshKey={scheduleRefreshKey}
          />
        )}
      </Stack>

      <CategoryEditorDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSaved={(saved) => {
          replaceCategory(saved);
          setScheduleRefreshKey((current) => current + 1);
          setDialogOpen(false);
        }}
      />
      <CategoryImageDialog
        category={imageCategory}
        open={!!imageCategory}
        onClose={() => setImageCategory(null)}
        onSaved={replaceCategory}
      />
    </Container>
  );
}
